import { NgFor, NgIf } from '@angular/common';
import { Component, computed, Input, OnInit, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
// import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { ApiService } from 'src/app/core/services/api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { InnovationRequestTypeConfig } from 'src/app/models/InnovationRequestTypeConfig';
import { NewRequestSummaryField } from 'src/app/models/NewRequestReadonlyField';
import { NewRequestStatus } from 'src/app/models/NewRequestStatus';
import { LegalPersonQueryResponse } from 'src/app/models/OpenData.LegalPersonQueryResponse';
import { PhysicalPersonQueryResponse, QueryResponse } from 'src/app/models/OpenData.PhysicalPersonQueryResponse';
import { StandardRequiredService } from 'src/app/models/StandardRequiredService';
import { AdminMessagesComponent, AdminMessagesType } from 'src/app/shared/components/admin-messages/admin-messages.component';
import { BelenderWidgetComponent } from 'src/app/shared/components/belender-widget/belender-widget.component';
import { NewRequestSummaryFieldsComponent } from 'src/app/shared/components/new-request-summary-fields/new-request-summary-fields.component';
import { QrCodeComponent } from 'src/app/shared/components/qr-code/qr-code.component';
import { DNI, INVALID, SEPARATOR } from 'src/app/shared/constants/validation.constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { environment } from 'src/environments/environment';
import { InsideDisableComponent } from '../../../utils/inside-disable/inside-disable.component';
import { LoadingComponent } from '../../../utils/loading/loading.component';
import { InnovationRequestService } from '../innovation-requests/innovation-request.service';
import { InnovationRequestsFilterService } from '../innovation-requests/innovation-requests-filter.service';
import { NewInnovationRequestService } from './new-innovation-request.service';
import { LabeledValue } from 'src/app/models/LabeledValue';

@Component({
    selector: 'app-new-innovation-request',
    templateUrl: './new-innovation-request.component.html',
    styleUrl: './new-innovation-request.component.scss',
    standalone: true,
    imports: [
        ToastModule,
        PrimeTemplate,
        NgFor,
        ConfirmDialogModule,
        NgIf,
        RadioButtonModule,
        FormsModule,
        TooltipModule,
        LoadingComponent,
        InsideDisableComponent,
        ReactiveFormsModule,
        InputTextModule,
        DatePickerModule,
        SelectModule,
        TranslateModule,
        QrCodeComponent,
        // FileUploadModule,
        NewRequestSummaryFieldsComponent,
        AdminMessagesComponent,
        BelenderWidgetComponent
    ],
})
export class NewInnovationRequestComponent implements OnInit {

    @ViewChild('adminMessages') adminMessagesComponent: AdminMessagesComponent;

    queryResponse: QueryResponse;

    @Input() newRequestStatus: NewRequestStatus = { title: 'New request', step: 1 };
    @Input() trustedConsentUrl: any;

    // protected readonly MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB
    // protected readonly ACCEPTED_FILE_TYPES = 'image/*,application/pdf';

    isLoading = false;
    formIsDisabled = false;

    currentBelenderCircuit: 2 | 4 | 6 = 2;
    currentDocumentationPack = 'SELFEMPLOYEE';

    private readonly waitingMessagesTranslationKeys = ['NEW_REQUEST.WAITING_MESSAGES.WAIT_1', 'NEW_REQUEST.WAITING_MESSAGES.WAIT_2', 'NEW_REQUEST.WAITING_MESSAGES.WAIT_3'];
    waitingMessages = [];

    private invalidFields = [];
    private requiredFieldsMissing = false;

    readonly requestTypeConfigs: InnovationRequestTypeConfig[] = [
        {
            id: 'PhysicalPersonSelfemployee',
            label: 'PERSON_TYPES.01_ABBRV',
            documentationPack: 'SELFEMPLOYEE',
            belenderCircuit: 2,
            availableAccessTypes: [{ id: 'Clave', label: 'CLAVE_MOVIL' }],
            defaultAccessTypeId: 'Clave'
        },
        {
            id: 'PhysicalPersonIndividual',
            label: 'PERSON_TYPES.GUARANTOR_ABBV',
            documentationPack: 'INDIVIDUAL',
            belenderCircuit: 2,
            availableAccessTypes: [{ id: 'Clave', label: 'CLAVE_MOVIL' }],
            defaultAccessTypeId: 'Clave'
        },
        {
            id: 'LegalPerson',
            label: 'PERSON_TYPES.02_ABBRV',
            documentationPack: 'COMPANY',
            belenderCircuit: 6,
            availableAccessTypes: [
                { id: 'ClientCertificate', label: 'DIG_CERT_CC' },
                // { id: 'VideoId', label: 'DIG_CERT_VIDEO_ID' }
            ],
            defaultAccessTypeId: 'ClientCertificate'
        }
    ];

    selectedRequestType = this.requestTypeConfigs[0].id;
    availableAccessTypes: StandardRequiredService[] = this.requestTypeConfigs[0].availableAccessTypes;
    selectedRequestorType = 'PhysicalPerson';
    selectedAccessType = signal(this.requestTypeConfigs[0].defaultAccessTypeId);

    readonly documentTypeOptions = [
        { id: 'DNI', label: 'DNI' },
        { id: 'NIE', label: 'NIE' }
    ];

    @Input() belenderStatus: {
        actualStatus: string;
        actualStatusMessage: string;
    } = { actualStatus: '', actualStatusMessage: '' };

    isLoadingRequest = false;

    adminMessagesType = computed<AdminMessagesType>(() => {
        switch (this.selectedAccessType()) {
            case 'Clave':
                return 'clavePermissive';
            case 'ClientCertificate':
                return 'clientCertificatePermissive';
            default:
                return 'clavePermissive';
        }
    });
    isAdminOk = true;

    fields: NewRequestSummaryField[] = [
        {
            controlName: 'companyName',
            labelKey: 'NEW_REQUEST.COMPANY_NAME',
            type: 'text'
        },
        {
            controlName: 'companyId',
            labelKey: 'NEW_REQUEST.TIN',
            type: 'text'
        },
        {
            controlName: 'firstName',
            labelKey: 'NEW_REQUEST.NAME',
            type: 'text'
        },
        {
            controlName: 'firstSurname',
            labelKey: 'NEW_REQUEST.FIRST_SURNAME',
            type: 'text'
        },
        {
            controlName: 'secondSurname',
            labelKey: 'NEW_REQUEST.SECOND_SURNAME',
            type: 'text'
        },
        {
            controlName: 'personId',
            labelKey: 'NEW_REQUEST.DOCUMENT_NUMBER',
            type: 'text'
        },
        {
            controlName: 'birthDate',
            labelKey: 'NEW_REQUEST.BIRTH_DATE',
            type: 'date'
        },
        {
            controlName: 'supportNumber',
            labelKey: 'NEW_REQUEST.SUPPORT_NUMBER',
            type: 'text'
        },
        {
            controlName: 'email',
            labelKey: 'NEW_REQUEST.EMAIL',
            type: 'text'
        },
        {
            controlName: 'phoneNumber',
            labelKey: 'NEW_REQUEST.PHONE',
            type: 'text'
        }
    ];

    belenderWidgetLangOptions: LabeledValue[] = [
        { label: 'LANGUAGES.ES', value: 'es' },
        { label: 'LANGUAGES.CA', value: 'ca' },
        { label: 'LANGUAGES.EN', value: 'en' }
    ];

    constructor(
        private readonly api: ApiService,
        private readonly router: Router,
        private readonly translate: TranslateService,
        private readonly toastService: ToastService,
        public readonly innovationRequestService: InnovationRequestService,
        public readonly newInnovationRequestService: NewInnovationRequestService,
        private readonly sanitizer: DomSanitizer,
        private readonly utilsService: UtilsService,
        public readonly innovationRequestsFilterService: InnovationRequestsFilterService
    ) { }

    ngOnInit(): void {
        this.waitingMessages = this.waitingMessagesTranslationKeys.map(key => this.translate.instant(key));
        this.newInnovationRequestService.resetRequestorAndRequestTypes();
        this.applyRequestTypeConfig(this.requestTypeConfigs[0]);
        this.initStepOne();
    }

    /**
     * Aplica la configuración del tipo de solicitud seleccionado:
     * - Establece el paquete documental internamente
     * - Actualiza los tipos de acceso disponibles
     * - Establece el circuito Belender
     * - Preselecciona el tipo de acceso por defecto
     */
    applyRequestTypeConfig(config: InnovationRequestTypeConfig): void {
        this.currentDocumentationPack = config.documentationPack;
        this.currentBelenderCircuit = config.belenderCircuit;
        this.availableAccessTypes = config.availableAccessTypes;
        this.selectedAccessType.set(config.defaultAccessTypeId);
        this.selectedRequestType = config.id;

        // Determinar el requestorType según el tipo de solicitud
        if (config.id === 'LegalPerson') {
            this.selectedRequestorType = 'LegalPerson';
        } else {
            this.selectedRequestorType = 'PhysicalPerson';
        }
    }

    // receiveMessage = (event: MessageEvent) => {
    //     const messageFromBelender = event.data as BelenderWebShocketMessage;

    //     if (messageFromBelender && messageFromBelender.type == "websocket") {
    //         if (BelenderStatusCodes.includes(messageFromBelender.data.status_code)) {
    //             // Assert form está deshabilitado
    //             if (!this.formIsDisabled) this.formIsDisabled = true;

    //             this.belenderStatus.actualStatus = messageFromBelender.data.status_code;
    //             this.belenderStatus.actualStatusMessage = messageFromBelender.data.status_message;

    //             if (this.belenderStatus.actualStatus === "consent_process_completed") {
    //                 this.toastService.showSuccess('TOAST.SUCCESS', 'NEW_REQUEST.CREATED', true);
    //             }
    //         }
    //     }
    // };

    detailButtonIsVisible(): boolean {
        return this.belenderStatus.actualStatus === "consent_process_completed";
    };

    isProd(): boolean {
        return environment.prodEnv;
    }

    isLegalPersonSelected(): boolean {
        return this.selectedRequestorType === 'LegalPerson';
    }

    updateTrustedUrl(newUrl: string): void {
        this.trustedConsentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(newUrl);
    }

    initStepOne(): void {
        if (this.newRequestStatus.step === 1) {
            if (this.selectedRequestorType != '' && this.selectedAccessType() != '') {
                this.newInnovationRequestService.setForm(this.selectedRequestorType, this.selectedAccessType());
            }
        }
    }

    goToLastStep(): void {
        if (this.newInnovationRequestService.isFinishingOTP) {
            this.newInnovationRequestService.isFinishingOTP = false;
            window.location.reload();
        }
        this.newRequestStatus = { title: 'Request summary', step: 2 };
    }

    continueOTP() {
        this.newRequestStatus = { title: 'Continue OTP', step: 3};
    }

    goToDetail(): void {
        if (this.queryResponse) {
            this.isLoadingRequest = true;
            this.router.navigate([`/innovation/request-detail/${this.queryResponse.queryId}`])
                .catch(error => { console.error(error); })
                .finally(() => this.isLoadingRequest = true);
        }
    }

    // #region Submit Methods
    async onSubmit(): Promise<void> {
        this.formIsDisabled = true;

        if (!this.newInnovationRequestService.myForm.valid) {
            this.formIsDisabled = false;
            this.checkValidationErrors();
            return;
        }

        try {
            this.isLoading = true;

            if (this.selectedRequestorType === 'LegalPerson') {
                await this.submitLegalPersonRequest();
            } else {
                await this.submitPhysicalPersonRequest();
            }

            await this.handleSubmitSuccess();
        } catch (error) {
            await this.handleSubmitError(error);
        }
    }

    private async submitPhysicalPersonRequest(): Promise<void> {
        const requestData = this.preparePhysicalPersonData();
        this.queryResponse = await this.api.post<PhysicalPersonQueryResponse>('innovation/new-physical-request', requestData);
    }

    private async submitLegalPersonRequest(): Promise<void> {
        const requestData = this.prepareLegalPersonData();
        this.queryResponse = await this.api.post<LegalPersonQueryResponse>('innovation/new-legal-request', requestData);
    }

    private preparePhysicalPersonData(): Record<string, unknown> {
        const formData = { ...this.newInnovationRequestService.myForm.value };

        // Formatear fecha de nacimiento si existe
        if (this.newInnovationRequestService.myForm.get('birthDate')?.value) {
            const birthDate = new Date(formData.birthDate);
            formData.birthDate = this.utilsService.formatLocalYYYYMMDD(birthDate);
        }

        // Segundo apellido por defecto
        if (this.utilsService.isStringNullOrEmpty(formData.secondSurname)) {
            formData.secondSurname = '-';
        }

        // Limpiar campos según tipo de documento
        if (this.newInnovationRequestService.selectedDocumentType === 'DNI') {
            formData.birthDate = null;
            formData.supportNumber = null;
        }

        // Email siempre null para persona física
        formData.email = null;

        return this.addCommonRequestData(formData);
    }

    private prepareLegalPersonData(): Record<string, unknown> {
        const formData = { ...this.newInnovationRequestService.myForm.value };
        if ('personId' in formData) {
            delete formData['personId'];
        }

        // Segundo apellido por defecto
        if (this.utilsService.isStringNullOrEmpty(formData.secondSurname)) {
            formData.secondSurname = '-';
        }

        // NIF de empresa en mayúsculas
        if (formData.companyId) {
            formData.companyId = formData.companyId.toUpperCase();
        }

        return this.addCommonRequestData(formData);
    }

    private addCommonRequestData(formData: Record<string, unknown>): Record<string, unknown> {
        if (typeof formData['personId'] === 'string' && formData['personId']) {
            formData['personId'] = (formData['personId'] as string).toUpperCase();
        } else {
            delete formData['personId'];
        }
        formData['documentationPack'] = this.currentDocumentationPack;
        formData['personType'] = this.getPersonTypeCode();
        formData['belenderCircuit'] = this.currentBelenderCircuit;

        return formData;
    }

    private async handleSubmitSuccess(): Promise<void> {
        if (!this.validateResponse(this.queryResponse)) {
            this.isLoading = false;
            this.formIsDisabled = false;
            return;
        }

        if (this.queryResponse.consentUrl != null) {
            this.updateTrustedUrl(this.queryResponse.consentUrl);
        }

        this.isLoading = false;
        this.newRequestStatus = { title: 'Request summary', step: 2 };

        await this.innovationRequestsFilterService.search(true);
    }

    private async handleSubmitError(error: any): Promise<void> {
        this.isLoading = false;
        this.formIsDisabled = false;

        if (error?.status === 422 || error?.error?.status === 422) {
            this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.422_ERROR', true);
            await this.adminMessagesComponent.setMessages();
        } else if (error?.status === 400 && error.error === INVALID + SEPARATOR + DNI) {
            this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.INVALID_DNI', true);
        } else {
            this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.ERROR', true);
        }
    }
    // #endregion

    checkValidationErrors(): void {
        for (const key in this.newInnovationRequestService.myForm.controls) { // LOOP THROUGH ALL FORM CONTROLS
            if (Object.prototype.hasOwnProperty.call(this.newInnovationRequestService.myForm.controls, key)) { // CHECK IF THE PROPERTY BELONGS TO THE OBJECT
                const control = this.newInnovationRequestService.myForm.get(key);
                if (control.invalid) {
                    this.checkFieldIsRequiredOrInvalid(control, key);
                }
            }
        }
        this.showValidationErrors();

        this.invalidFields = [];
        this.requiredFieldsMissing = false;
    }

    checkFieldIsRequiredOrInvalid(control: AbstractControl, key: string): void {
        if (control.errors['required']) {
            if (!this.requiredFieldsMissing) this.requiredFieldsMissing = true; // SET THE FLAG TO TRUE ONLY ONCE

            // Mark control as touched and dirty for template class bindings
            control.markAsTouched();
            control.markAsDirty();

            // For elements with formControlName, also add classes to DOM
            const formField = document.querySelector(`[formControlName="${key}"]`);
            if (formField) {
                formField.classList.add('ng-dirty', 'ng-touched'); // ADD THE CLASSES TO SHOW THE ERROR
            }
        } else {
            this.invalidFields.push(`NEW_REQUEST.INVALID_FORM.${key}`);  // ADD THE INVALID FIELD TO THE ARRAY
        }
    }

    /**
     * if a required field is not filled, it shows a toast with a message,
     * otherwise it shows a toast with the invalid fields
     */
    showValidationErrors(): void {
        if (this.requiredFieldsMissing) {
            this.toastService.showError('TOAST.INVALID', 'NEW_REQUEST.ALL_FIELDS_REQUIRED', false, null, 'newRequestErrors');
        } else {
            this.toastService.showError('TOAST.INVALID', 'NEW_REQUEST.INVALID_FORM_DETAIL', false, 5000, 'newRequestErrors', this.invalidFields);
        }
    }

    validateResponse(physicalPersonQueryResponse: QueryResponse): boolean {
        if (physicalPersonQueryResponse.statusCode == 400 || physicalPersonQueryResponse.queryId == null) {
            this.toastService.showError(`Error ${physicalPersonQueryResponse.statusCode}`, `${physicalPersonQueryResponse?.formattedErrors}`, false, 10000);  //TODO: traudcir
            return false;
        }

        return true;
    }

    isFieldInvalid(fieldName: string, errorName: string): boolean {
        const field = this.newInnovationRequestService.myForm.get(fieldName);

        if (field.hasError(errorName) && (field.dirty || field.touched)) {
            return true;
        } else {
            return false;
        }
    }

    onRequestTypeChange(event: SelectChangeEvent): void {
        const selectedConfig = this.requestTypeConfigs.find(config => config.id === event.value);
        if (selectedConfig) {
            this.applyRequestTypeConfig(selectedConfig);
            this.newInnovationRequestService.setForm(this.selectedRequestorType, this.selectedAccessType());
        }
    }

    onAccessTypeChange(event: SelectChangeEvent): void {
        this.selectedAccessType.set(event.value);
        this.newInnovationRequestService.setForm(this.selectedRequestorType, this.selectedAccessType());
    }

    onDocumentTypeChange(event: SelectChangeEvent): void {
        this.newInnovationRequestService.updateDocumentValidators(event.value);
    }

    private getPersonTypeCode(): string {
        switch (this.selectedRequestType) {
            case 'PhysicalPersonIndividual':
                return '00';
            case 'PhysicalPersonSelfemployee':
                return '01';
            case 'LegalPerson':
                return '02';
            default:
                return '00';
        }
    }

    // #region File Methods
    // protected onFileSelect(event: any, controlName: 'personIdFrontPhoto' | 'personIdBackPhoto'): void {
    //     const file = event.files[0];

    //     if (!file || !this.validateFile(file)) return;

    //     const reader = new FileReader();
    //     reader.onload = () => {
    //         const base64String = reader.result as string;
    //         const cleanBase64 = base64String.split(',')[1];    // Delete the "data:image/...;base64," prefix
    //         this.newInnovationRequestService.myForm.get(controlName)?.setValue(cleanBase64);
    //     };
    //     reader.readAsDataURL(file);
    // }

    // protected onFileRemove(controlName: 'personIdFrontPhoto' | 'personIdBackPhoto'): void {
    //     this.newInnovationRequestService.myForm.get(controlName)?.setValue('');
    // }

    // private validateFile(file: File): boolean {
    //     const isValidSize = file.size <= this.MAX_FILE_SIZE;
    //     const mime = (file.type || '').toLowerCase();
    //     let isValidType = false;
    //     if (mime) {
    //         isValidType = mime.startsWith('image/') || mime === 'application/pdf';
    //     } else {
    //         const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    //         const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
    //         isValidType = imageExts.includes(ext) || ext === 'pdf';
    //     }
    //     return isValidType && isValidSize;
    // }
    // #endregion
}
